import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { useIntl, defineMessages } from "react-intl";
import classNames from "classnames";
import SpinnerLoader from "@app/neva/components/spinnerLoader/spinnerLoader";
import Svg from "@natera/material/lib/svg";
import { VideoPlayer } from "@app/components";
import { CaptionsOptions, IVideo } from "@app/components/videoPlayer";
import { IntlContext, Language } from "@app/provider";
import { VideoType } from "@app/neva/models/apiControllerModel";
import { ActionTypes, VideoAuthorCode } from "@app/neva/models/constants";
import { generateActionUserEvent } from "@app/neva/helpers/userEventGenerator";
import { getNevaHeapVideoEvents } from "@app/neva/helpers/getNevaHeapVideoEvents";
import { testTypeToBotId } from "@app/neva/helpers/testTypeToBotId";
import { TestType } from "@app/provider/testData/types";
import { useBindNevaHeapVideo } from "@app/neva/hooks/useBindNevaHeapVideo";
import { EventsContext, NevaContext } from "@app/neva/provider";
import { useLogEvent } from "@app/neva/hooks";
import ANThumbnailEven from "@app/neva/assets/anna_norvez_thumbnail_even.png";
import ANThumbnailOdd from "@app/neva/assets/anna_norvez_thumbnail_odd.png";
import NTThumbnailEven from "@app/neva/assets/nicole_truitt_thumbnail_even.png";
import NTThumbnailOdd from "@app/neva/assets/nicole_truitt_thumbnail_odd.png";
import errorIcon from "@app/neva/assets/svg/error-icon.svg";

const VIDEO_DELAY_TIMEOUT = 10000;

const WISTIA_SPANISH_CAPTIONS_LANGUAGE = "spa";

type WistiaVideo = IVideo & {
  unbind: (event: string, handler: (arg?: unknown) => void) => void;
};

interface Props {
  video: VideoType;
  index: number;
  isLatestVideo: boolean;
  registerVideo?: (videoId: string, instance: IVideo) => void;
  unregisterVideo?: (videoId: string) => void;
  onVideoPlay?: (videoId: string) => void;
}

const messages = defineMessages({
  videoErrorTitle: {
    id: "videoErrorTitle",
    defaultMessage: "Sorry, we couldn't load the video",
  },
});

const VideoPlayerWrapper: FC<Props> = ({
  video,
  index,
  isLatestVideo,
  registerVideo,
  unregisterVideo,
  onVideoPlay,
}) => {
  const { bindNevaHeapVideo } = useBindNevaHeapVideo();
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);
  const captionsOptions: CaptionsOptions =
    currentLanguage === Language.ES
      ? { captionsEnabled: true, language: WISTIA_SPANISH_CAPTIONS_LANGUAGE }
      : { captionsEnabled: false };
  const {
    eventsProviderState: { showTyping },
    userEventHandler,
  } = useContext(EventsContext);
  const { logEvent } = useLogEvent();
  const { botId } = useContext(NevaContext);
  const isPanorama = botId === testTypeToBotId(TestType.PANORAMA);
  const heapVideoEvents = getNevaHeapVideoEvents(botId);
  const [isVideoPlayerLoading, setIsVideoPlayerLoading] = useState(true);
  const [hasVideoError, setHasVideoError] = useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout>(undefined);
  const isLatestVideoRef = useRef(isLatestVideo);
  isLatestVideoRef.current = isLatestVideo;

  const isLatestPanoramaPlayer = () => isPanorama && isLatestVideoRef.current;

  const videoThumbnailIdentifier = (
    authorCode: VideoAuthorCode,
    index: number
  ) => {
    const isEven = index % 2;
    return authorCode === VideoAuthorCode.AN
      ? isEven
        ? ANThumbnailEven
        : ANThumbnailOdd
      : isEven
        ? NTThumbnailEven
        : NTThumbnailOdd;
  };

  useEffect(() => {
    if (!registerVideo && !onVideoPlay) {
      return;
    }

    let isMounted = true;
    let videoInstance: IVideo | null = null;

    const playHandler = () => {
      onVideoPlay?.(video.videoId);
    };

    const bindObj = {
      id: video.videoId,
      onReady: (video$: IVideo) => {
        if (!isMounted) {
          return;
        }
        videoInstance = video$;
        registerVideo?.(video.videoId, video$);
        if (onVideoPlay) {
          video$.bind("play", playHandler);
        }
      },
    };

    window._wq = window._wq || [];
    window._wq.push(bindObj);

    return () => {
      isMounted = false;
      if (videoInstance && onVideoPlay) {
        (videoInstance as WistiaVideo).unbind("play", playHandler);
      }
      unregisterVideo?.(video.videoId);
      window._wq.push({ revoke: bindObj });
    };
  }, [video.videoId, registerVideo, unregisterVideo, onVideoPlay]);

  useEffect(() => {
    if (isVideoPlayerLoading && !showTyping) {
      timeoutIdRef.current = setTimeout(() => {
        setHasVideoError(true);
        setIsVideoPlayerLoading(false);

        if (heapVideoEvents) {
          logEvent({
            eventAction: heapVideoEvents.timeoutEvent,
            eventLabel: "User Action",
          });
        }

        if (isLatestPanoramaPlayer()) {
          userEventHandler(
            generateActionUserEvent(ActionTypes.PANORAMA_VIDEO_TIMEOUT)
          );
        }
      }, VIDEO_DELAY_TIMEOUT);
    }

    if (!isVideoPlayerLoading) {
      clearTimeout(timeoutIdRef.current);
    }

    return () => {
      clearTimeout(timeoutIdRef.current);
    };
  }, [isVideoPlayerLoading, showTyping]);

  const isVideoLoaded = !isVideoPlayerLoading && !hasVideoError;

  useEffect(() => {
    if (isVideoLoaded && isLatestPanoramaPlayer()) {
      userEventHandler(
        generateActionUserEvent(ActionTypes.PANORAMA_VIDEO_LOADED)
      );
    }
  }, [isPanorama, isVideoLoaded]);

  return (
    <>
      {isVideoPlayerLoading && (
        <div
          className="video-message__loader-container"
          style={{
            backgroundImage: `url(${videoThumbnailIdentifier(
              video.authorCode,
              index
            )})`,
          }}
        >
          <SpinnerLoader />
        </div>
      )}
      {hasVideoError && (
        <div className="video-message__error-container">
          <div className="video-message__error-content">
            <Svg content={errorIcon} />
            <div className="video-message__error-title">
              {intl.formatMessage(messages.videoErrorTitle)}
            </div>
          </div>
        </div>
      )}
      <div
        className={classNames({
          "video-message__player-container-loading":
            isVideoPlayerLoading || hasVideoError,
        })}
      >
        <VideoPlayer
          id={video.videoId}
          thumbnailAltText={video.title}
          setIsVideoPlayerLoading={setIsVideoPlayerLoading}
          bindNevaHeapVideo={bindNevaHeapVideo}
          thumbnail={videoThumbnailIdentifier(video.authorCode, index)}
          captionsOptions={captionsOptions}
        />
      </div>
    </>
  );
};

export default VideoPlayerWrapper;
